import * as THREE from 'three'
import { Location } from '../../core/Location'
import { System } from './System'

const _dirN = new THREE.Vector3()
const _tmpMat = new THREE.Matrix4()
const _hits = []
const _raycaster = new THREE.Raycaster()

const _sphere = new THREE.Sphere()
const _inverseMatrix = new THREE.Matrix4()
const _localSphere = new THREE.Sphere()

const _localShapeMatrix = new THREE.Matrix4()

const _mesh = new THREE.Mesh()
_mesh.matrixAutoUpdate = false

const EPS = 1e-9

const loc1 = new Location()

export const HitTypes = {
  BLOCK: 'block',
  ENTITY: 'entity',
}

// Reusable hit result
const _hit = {
  point: new THREE.Vector3(),
  normal: new THREE.Vector3(),
  distance: 0,
  type: null, // 'block' | 'entity'
  block: null,
  chunk: null,
  voxel: new THREE.Vector3(),
  entity: null,
}

const _shapecastHit = {
  type: null, // entity|block
  entity: null,
  block: null,
  location: null,
}

function nextBoundaryDistanceX(x, dx, step) {
  if (step === 0) return Number.POSITIVE_INFINITY
  const boundary = step > 0 ? Math.floor(x + 0.5) + 0.5 : Math.floor(x + 0.5) - 0.5
  return (boundary - x) / dx
}

function nextBoundaryDistanceY(y, dy, step) {
  if (step === 0) return Number.POSITIVE_INFINITY
  const cell = Math.floor(y)
  const boundary = step > 0 ? cell + 1 : cell
  return (boundary - y) / dy
}

function nextBoundaryDistanceZ(z, dz, step) {
  if (step === 0) return Number.POSITIVE_INFINITY
  const boundary = step > 0 ? Math.floor(z + 0.5) + 0.5 : Math.floor(z + 0.5) - 0.5
  return (boundary - z) / dz
}

function composeBlockMatrix(ix, iy, iz) {
  return _tmpMat.compose(new THREE.Vector3(ix, iy, iz), new THREE.Quaternion(), new THREE.Vector3(1, 1, 1))
}

export class SpatialSystem extends System {
  constructor(client) {
    super(client)
  }

  /**
   * Unified raycast against voxels and entities.
   * @param {THREE.Vector3} origin
   * @param {THREE.Vector3} direction
   * @param {number} maxDistance
   * @param {object} options
   * @param {boolean} options.blocks - Check blocks (default: true)
   * @param {boolean} options.entities - Check entities (default: true)
   * @param {Set|Array} options.excludeEntities - Entity IDs to skip
   */
  raycast(origin, direction, maxDistance, options = {}) {
    const { blocks = true, entities = true, excludeEntities = null } = options

    if (maxDistance <= 0) return null

    _dirN.copy(direction)
    const len = _dirN.length()
    if (len < EPS) return null
    _dirN.divideScalar(len)

    _raycaster.near = 0
    _raycaster.far = maxDistance
    _raycaster.set(origin, _dirN)
    _raycaster.firstHitOnly = true

    let closestHit = null
    let closestDist = maxDistance

    // --- Check entities first (usually fewer than voxels to traverse) ---
    if (entities) {
      const entityHit = this._raycastEntities(origin, _dirN, closestDist, excludeEntities)
      if (entityHit && entityHit.distance < closestDist) {
        closestHit = entityHit
        closestDist = entityHit.distance
      }
    }

    // --- DDA voxel traversal ---
    if (blocks) {
      const blockHit = this._raycastBlocks(origin, _dirN, closestDist)
      if (blockHit && blockHit.distance < closestDist) {
        closestHit = blockHit
      }
    }

    return closestHit
  }

  /**
   * Raycast against all entities with hitboxes.
   */
  _raycastEntities(origin, direction, maxDistance, excludeEntities) {
    let closest = null
    let closestDist = maxDistance
    const maxDistSq = maxDistance * maxDistance
    for (const entity of this.client.entities.all()) {
      if (excludeEntities?.has(entity.id)) continue
      if (!entity.hitbox?.geometry?.boundsTree) continue

      // Cheap squared-distance cull
      const distSq = entity.position.distanceToSquared(origin)
      if (distSq > maxDistSq * 2) continue

      // Update raycaster range to early-out faster
      _raycaster.far = closestDist

      // Set up the shared mesh with entity's hitbox and transform
      _mesh.geometry = entity.hitbox.geometry
      _mesh.matrixWorld.copy(entity.matrixWorld) // or compose from entity.position/quaternion

      _hits.length = 0
      _mesh.raycast(_raycaster, _hits)

      if (_hits.length && _hits[0].distance < closestDist) {
        const h = _hits[0]
        closest = {
          point: h.point.clone(),
          normal: h.face?.normal?.clone() || new THREE.Vector3(0, 1, 0),
          distance: h.distance,
          type: 'entity',
          entity: entity,
          block: null,
          chunk: null,
          voxel: null,
        }
        closestDist = h.distance
      }
    }

    return closest
  }

  /**
   * Compute approximate normal for box intersection.
   */
  _boxHitNormal(box, point) {
    const normal = new THREE.Vector3()
    const center = new THREE.Vector3()
    box.getCenter(center)

    const size = new THREE.Vector3()
    box.getSize(size)

    const rel = point.clone().sub(center)
    const abs = new THREE.Vector3(Math.abs(rel.x) / size.x, Math.abs(rel.y) / size.y, Math.abs(rel.z) / size.z)

    if (abs.x > abs.y && abs.x > abs.z) {
      normal.set(Math.sign(rel.x), 0, 0)
    } else if (abs.y > abs.z) {
      normal.set(0, Math.sign(rel.y), 0)
    } else {
      normal.set(0, 0, Math.sign(rel.z))
    }

    return normal
  }

  /**
   * DDA voxel raycast (your existing logic).
   */
  _raycastBlocks(origin, direction, maxDistance) {
    let ix = Math.floor(origin.x + 0.5)
    let iy = Math.floor(origin.y)
    let iz = Math.floor(origin.z + 0.5)

    const stepX = direction.x > 0 ? 1 : direction.x < 0 ? -1 : 0
    const stepY = direction.y > 0 ? 1 : direction.y < 0 ? -1 : 0
    const stepZ = direction.z > 0 ? 1 : direction.z < 0 ? -1 : 0

    let tMaxX = stepX ? Math.abs(nextBoundaryDistanceX(origin.x, direction.x, stepX)) : Number.POSITIVE_INFINITY
    let tMaxY = stepY ? Math.abs(nextBoundaryDistanceY(origin.y, direction.y, stepY)) : Number.POSITIVE_INFINITY
    let tMaxZ = stepZ ? Math.abs(nextBoundaryDistanceZ(origin.z, direction.z, stepZ)) : Number.POSITIVE_INFINITY

    const tDeltaX = stepX ? Math.abs(1 / direction.x) : Number.POSITIVE_INFINITY
    const tDeltaY = stepY ? Math.abs(1 / direction.y) : Number.POSITIVE_INFINITY
    const tDeltaZ = stepZ ? Math.abs(1 / direction.z) : Number.POSITIVE_INFINITY

    let t = 0

    while (t <= maxDistance + EPS) {
      loc1.voxel.set(ix, iy, iz)

      const chunk = this.client.chunks.get(loc1.chunkId)
      if (chunk) {
        const block = chunk.getBlockAtIdx(loc1.voxelIdx)
        if (block && block.hitbox) {
          _mesh.geometry = block.hitbox.geometry
          _mesh.matrixWorld.copy(composeBlockMatrix(ix, iy, iz))

          _hits.length = 0
          _raycaster.far = maxDistance
          _mesh.raycast(_raycaster, _hits)

          if (_hits.length) {
            const h = _hits[0]
            return {
              point: h.point.clone(),
              normal: h.face.normal.clone(),
              distance: h.distance,
              type: 'block',
              block: block,
              chunk: chunk,
              voxel: new THREE.Vector3(ix, iy, iz),
              entity: null,
            }
          }
        }
      }

      if (tMaxX < tMaxY) {
        if (tMaxX < tMaxZ) {
          ix += stepX
          t = tMaxX
          tMaxX += tDeltaX
        } else {
          iz += stepZ
          t = tMaxZ
          tMaxZ += tDeltaZ
        }
      } else {
        if (tMaxY < tMaxZ) {
          iy += stepY
          t = tMaxY
          tMaxY += tDeltaY
        } else {
          iz += stepZ
          t = tMaxZ
          tMaxZ += tDeltaZ
        }
      }
    }

    return null
  }

  // /**
  //  * Find all entities/blocks overlapping a sphere.
  //  * @param {THREE.Vector3} origin
  //  * @param {number} radius
  //  * @param {object} options
  //  * @returns {Array<{type: 'block'|'entity', entity?, block?, chunk?, voxel?}>}
  //  */
  // overlapSphere(origin, radius, options = {}) {
  //   const { blocks = true, entities = true, excludeEntities = null } = options

  //   const results = []

  //   // --- Entities ---
  //   if (entities) {
  //     // const radiusSq = radius * radius

  //     for (const entity of this.client.entities.entities) {
  //       if (excludeEntities?.has(entity.id)) continue
  //       if (!entity.hitbox?.geometry?.boundsTree) continue

  //       // cheap center distance check first
  //       const distSq = entity.position.distanceToSquared(origin)
  //       if (distSq > (radius + entity.boundingRadius) ** 2) continue

  //       // transform sphere into entity's local space
  //       _inverseMatrix.copy(entity.matrixWorld).invert()
  //       _localSphere.center.copy(origin).applyMatrix4(_inverseMatrix)
  //       _localSphere.radius = radius // assumes uniform scale

  //       // BVH shapecast
  //       const hit = entity.hitbox.geometry.boundsTree.shapecast({
  //         intersectsBounds: box => box.intersectsSphere(_localSphere),
  //         intersectsTriangle: tri => tri.intersectsSphere(_localSphere),
  //       })

  //       if (hit) {
  //         results.push({ type: 'entity', entity })
  //       }
  //     }
  //   }

  //   // --- Blocks ---
  //   if (blocks) {
  //     // Check all voxels the sphere could touch
  //     const minX = Math.floor(origin.x - radius + 0.5)
  //     const maxX = Math.floor(origin.x + radius + 0.5)
  //     const minY = Math.floor(origin.y - radius)
  //     const maxY = Math.floor(origin.y + radius)
  //     const minZ = Math.floor(origin.z - radius + 0.5)
  //     const maxZ = Math.floor(origin.z + radius + 0.5)

  //     for (let ix = minX; ix <= maxX; ix++) {
  //       for (let iy = minY; iy <= maxY; iy++) {
  //         for (let iz = minZ; iz <= maxZ; iz++) {
  //           loc1.voxel.set(ix, iy, iz)
  //           const chunk = this.client.chunks.get(loc1.chunkId)
  //           if (!chunk) continue

  //           const block = chunk.getBlockAtIdx(loc1.voxelIdx)
  //           if (!block?.hitbox?.geometry?.boundsTree) continue

  //           // Transform sphere into block's local space
  //           _inverseMatrix.copy(composeBlockMatrix(ix, iy, iz)).invert()
  //           _localSphere.center.copy(origin).applyMatrix4(_inverseMatrix)
  //           _localSphere.radius = radius

  //           const hit = block.hitbox.geometry.boundsTree.shapecast({
  //             intersectsBounds: box => box.intersectsSphere(_localSphere),
  //             intersectsTriangle: tri => tri.intersectsSphere(_localSphere),
  //           })

  //           if (hit) {
  //             results.push({
  //               type: 'block',
  //               block,
  //               chunk,
  //               voxel: new THREE.Vector3(ix, iy, iz),
  //             })
  //           }
  //         }
  //       }
  //     }
  //   }

  //   return results
  // }

  /**
   * General shapecast using a mesh with BVH.
   * @param {THREE.Mesh} mesh - Must have geometry.boundsTree, and up-to-date matrixWorld
   * @param {object} options
   * @returns {Array<{type: 'block'|'entity', entity?, block?, chunk?, voxel?}>}
   */
  shapecast(mesh, options = {}) {
    const { blocks = true, entities = true, excludeEntities = null } = options

    const geometry = mesh.geometry
    if (!geometry.boundsTree) {
      geometry.computeBoundsTree()
      // console.warn('shapecast mesh needs a BVH')
      // return []
    }

    // const results = []

    // Get world-space bounding sphere for broad phase
    if (!geometry.boundingSphere) geometry.computeBoundingSphere()
    _sphere.copy(geometry.boundingSphere).applyMatrix4(mesh.matrixWorld)

    // --- Entities ---
    if (entities) {
      for (const entity of this.client.entities.entities) {
        if (excludeEntities?.has(entity.id)) continue
        if (!entity.hitbox?.geometry?.boundsTree) continue

        // Broad phase
        const distSq = entity.position.distanceToSquared(_sphere.center)
        if (distSq > (_sphere.radius + entity.boundingRadius) ** 2) continue

        // Transform query shape into entity's local space
        _inverseMatrix.copy(entity.matrixWorld).invert()
        _localShapeMatrix.multiplyMatrices(_inverseMatrix, mesh.matrixWorld)

        const hit = entity.hitbox.geometry.boundsTree.bvhcast(geometry.boundsTree, _localShapeMatrix, {
          intersectsTriangles() {
            return true
          },
        })

        if (hit) {
          _shapecastHit.type = HitTypes.ENTITY
          _shapecastHit.entity = entity
          _shapecastHit.block = null
          _shapecastHit.location = null
          return _shapecastHit
        }
      }
    }

    // --- Blocks ---
    if (blocks) {
      const minX = Math.floor(_sphere.center.x - _sphere.radius + 0.5)
      const maxX = Math.floor(_sphere.center.x + _sphere.radius + 0.5)
      const minY = Math.floor(_sphere.center.y - _sphere.radius)
      const maxY = Math.floor(_sphere.center.y + _sphere.radius)
      const minZ = Math.floor(_sphere.center.z - _sphere.radius + 0.5)
      const maxZ = Math.floor(_sphere.center.z + _sphere.radius + 0.5)

      for (let ix = minX; ix <= maxX; ix++) {
        for (let iy = minY; iy <= maxY; iy++) {
          for (let iz = minZ; iz <= maxZ; iz++) {
            loc1.voxel.set(ix, iy, iz)
            const chunk = this.client.chunks.get(loc1.chunkId)
            if (!chunk) continue

            const block = chunk.getBlockAtIdx(loc1.voxelIdx)
            if (!block?.hitbox?.geometry?.boundsTree) continue

            _inverseMatrix.copy(composeBlockMatrix(ix, iy, iz)).invert()
            _localShapeMatrix.multiplyMatrices(_inverseMatrix, mesh.matrixWorld)

            const hit = block.hitbox.geometry.boundsTree.bvhcast(geometry.boundsTree, _localShapeMatrix, {
              intersectsTriangles() {
                return true
              },
            })

            if (hit) {
              _shapecastHit.type = HitTypes.BLOCK
              _shapecastHit.entity = null
              _shapecastHit.block = block
              _shapecastHit.location = loc1 // set above
              return _shapecastHit
            }
          }
        }
      }
    }

    return null
  }
}
